import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

/**
 * Syncs a detail screen's tab index with a `:tab` URL segment.
 *
 * @param {object} opts
 * @param {string} opts.basePath Path through `/detail/...` **without** trailing `/:tab` (e.g. `/invoice/1/detail`).
 * @param {string[]} opts.tabSlugs Allowed slugs in route order (non-empty; otherwise navigation is a no-op).
 * @param {string} [opts.paramName='tab'] Route param name (default `tab`).
 */
const LEGACY_TAB_SLUGS = {
  building: 'property',
  buildings: 'properties',
};

export function useDetailRouteTabs({ basePath, tabSlugs, paramName = 'tab' }) {
  const params = useParams();
  const tab = params[paramName];
  const navigate = useNavigate();
  const location = useLocation();

  const hasSlugs = Array.isArray(tabSlugs) && tabSlugs.length > 0;
  const defaultSlug = hasSlugs ? tabSlugs[0] : '';
  const legacySlug = tab ? LEGACY_TAB_SLUGS[tab] : null;
  const isValidTab = hasSlugs && Boolean(tab && tabSlugs.includes(tab));

  useEffect(() => {
    if (!basePath || !hasSlugs || !defaultSlug) return;
    if (legacySlug && tabSlugs.includes(legacySlug)) {
      navigate(`${basePath}/${legacySlug}${location.search || ''}`, {
        replace: true,
        state: location.state,
      });
      return;
    }
    if (!isValidTab) {
      navigate(`${basePath}/${defaultSlug}${location.search || ''}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [
    basePath,
    tab,
    legacySlug,
    isValidTab,
    defaultSlug,
    hasSlugs,
    navigate,
    location.state,
    location.search,
    tabSlugs,
  ]);

  const selectedIndex = useMemo(
    () => (isValidTab ? tabSlugs.indexOf(tab) : 0),
    [isValidTab, tab, tabSlugs],
  );

  const goToTab = useCallback(
    (index) => {
      if (!basePath || !hasSlugs) return;
      const slug = tabSlugs[index];
      if (!slug) return;
      const path = `${basePath}/${slug}`;
      const sameSlug = slug === tab;
      const hasQuery = Boolean(location.search);
      if (sameSlug && !hasQuery) return;
      if (sameSlug && hasQuery) {
        navigate(path, { replace: true, state: location.state });
        return;
      }
      navigate(path, { state: location.state });
    },
    [basePath, hasSlugs, tabSlugs, navigate, tab, location.search, location.state],
  );

  const navigateToTabSlug = useCallback(
    (slug, { replace = false, search = null } = {}) => {
      if (!basePath || !hasSlugs || !slug || !tabSlugs.includes(slug)) return;
      const path = search ? `${basePath}/${slug}${search}` : `${basePath}/${slug}`;
      navigate(path, { replace, state: location.state });
    },
    [basePath, hasSlugs, navigate, location.state, tabSlugs],
  );

  return { selectedIndex, tabSlug: tab, goToTab, navigateToTabSlug, isValidTab };
}

/**
 * Base path for `/.../propertyDetail/:buildingId` routes (with or without `/:tab` already present).
 */
export function usePropertyDetailMarkerBasePath(buildingId) {
  const { pathname } = useLocation();
  return useMemo(() => {
    if (!buildingId) return '';
    for (const segment of ['propertyDetail', 'buildingDetail']) {
      const marker = `/${segment}/${buildingId}`;
      const idx = pathname.indexOf(marker);
      if (idx !== -1) {
        return pathname.slice(0, idx + marker.length);
      }
    }
    return '';
  }, [pathname, buildingId]);
}
